import React from 'react'
import PropTypes from 'prop-types'
import strings from 'utils/strings'
import { isEmpty } from 'ramda'
import { useSelector } from 'hooks/redux'
import setTheme from 'theme'
import { Button, Flex, FlexCol, Paragraph } from 'components/patterns'
import {
  getDashListsDefinitions,
  getDashComponents,
  getUnassignedComponents,
} from 'selectors/dash'
import { DEFAULT_DRAWER_WIDTH } from 'utils/drawerConstants'
import { isEmptyOrNil } from 'utils/functional'
import { getDrawerWidth } from 'selectors/drawer'
import { themeColor } from 'utils/styles'
import TablePart from './TablePart'
import {
  ListHeaderAndIcon,
  Group,
  ListHeader,
  SectionHeader,
  StyledCollapsibleWidget,
  StyledPanelNumberColumn,
  StyledChecTable,
  UnAssignedPartHeaderIcon,
  UnAssignedPartHeader,
  UnassignedComponentTable,
} from './PartTableView.style'

const generateSlots = (locations, onlyShowIfPartExist, components) =>
  locations.reduce((acc, loc) => {
    const part = components[loc]
    if (onlyShowIfPartExist && !part) {
      return acc
    }

    if (!part) return acc

    return acc.concat({ ...part })
  }, [])

const generateTitles = (slots, label) =>
  label && slots.length !== 0 ? (
    <SectionHeader key={label} size="small" weight="bold">
      {label}
    </SectionHeader>
  ) : (
    false
  )

const generateDescription = (panelDescription, dashObjectDescription) =>
  panelDescription || dashObjectDescription || strings.no_data

const PartTableView = ({ onToggleListView = () => {} }) => {
  const components = useSelector(getDashComponents)
  const unassignedComponents = useSelector(getUnassignedComponents)
  const combinedLists = useSelector(getDashListsDefinitions, {
    id: '',
    showAll: true,
  })

  const themes = setTheme()
  const drawerWidth = useSelector(getDrawerWidth)

  const defaultDrawerWidth = drawerWidth === DEFAULT_DRAWER_WIDTH
  const definitions = combinedLists

  const columns = [
    {
      title: strings.location_title,
      dataIndex: 'location',
      key: 'location',
      width: 65,
      render: (loc) => <span>{loc || strings.n_a_lowercase}</span>,
    },
    {
      title: strings.desc_title,
      dataIndex: 'panelDescription',
      key: 'panelDescription',
      width: 258,
      render: (panelDescription, record) => {
        const description = generateDescription(
          panelDescription,
          record.dashObjectDescription
        )
        return description === strings.no_data ? (
          <StyledPanelNumberColumn>{description}</StyledPanelNumberColumn>
        ) : (
          <TablePart
            defaultDrawerWidth={defaultDrawerWidth}
            tablePart={record}
          />
        )
      },
    },
    {
      title: strings.partnumber_title,
      dataIndex: 'panelNumber',
      key: 'panelNumber',
      width: 137,
      render: (panelNumber, record) => (
        <span>{panelNumber || record.dashObjectName}</span>
      ),
    },
  ]

  const bomCodesColumn = {
    title: strings.bom_title,
    dataIndex: 'bom',
    key: 'bom',
    width: 115,
    render: (bom) => <span>{bom || strings.n_a_lowercase}</span>,
  }

  const capPartNumberColumn = {
    title: strings.cap_part_no,
    dataIndex: 'capPartNumber',
    key: 'capPartNumber',
    width: 115,
    render: (capPartNumber) => (
      <span>{capPartNumber || strings.n_a_lowercase}</span>
    ),
  }

  const capDescriptionColumn = {
    title: strings.cap_description,
    dataIndex: 'capDescription',
    key: 'capDescription',
    width: 258,
    render: (capDesc) => <span>{capDesc || strings.n_a_lowercase}</span>,
  }

  if (isEmpty(components)) return null

  const unassignedComponentsTable = !isEmptyOrNil(unassignedComponents) && (
    <UnassignedComponentTable key="unassigned-components-table">
      <ListHeaderAndIcon>
        <UnAssignedPartHeaderIcon
          icon="alert_exclamation_triangle_solid"
          fill={themes.badgeWarningColor}
        />
        <UnAssignedPartHeader>
          {strings.parts_without_locations}
        </UnAssignedPartHeader>
      </ListHeaderAndIcon>
      <Group>
        <StyledChecTable
          columns={
            defaultDrawerWidth
              ? columns
              : [
                  ...columns,
                  bomCodesColumn,
                  capPartNumberColumn,
                  capDescriptionColumn,
                ]
          }
          dataSource={Object.values(unassignedComponents)}
          rowKey={(record) =>
            `${record.panelNumber}${record.listLocation}-unassigned`
          }
          pagination={false}
          className={`builder-swga-table-unassigned-components`}
          size="small"
        />
      </Group>
    </UnassignedComponentTable>
  )

  const tables = Object.values(definitions).map((tableDefinition, index) => {
    const { label: groupLabel, groups } = tableDefinition

    const sortedGroups = Object.entries(groups).sort(
      ([, a], [, b]) => (a.order ?? 0) - (b.order ?? 0)
    )

    const slotsFromGoups = []

    sortedGroups.forEach(([key, value]) => {
      const { locations, onlyShowIfPartExist } = value
      const slots = generateSlots(locations, onlyShowIfPartExist, components)

      if (!isEmpty(slots)) {
        slotsFromGoups.push(slots)
      }
    })

    return (
      <FlexCol key={groupLabel} width="100%" gap="20px">
        <FlexCol gap="4px" width="100%">
          <ListHeaderAndIcon
            index={index}
            groupLength={Object.keys(groups).length}
          >
            <ListHeader key={groupLabel} index={index}>
              {groupLabel}
            </ListHeader>
          </ListHeaderAndIcon>
          {Boolean(slotsFromGoups.length) && (
            <Flex gap="4px">
              <Paragraph
                style={{ fontSize: 12, color: themeColor('blackSecondary') }}
              >
                {strings.return_to_list_view}
              </Paragraph>
              <Button link onClick={onToggleListView} size="small">
                {strings.list_view}
              </Button>
            </Flex>
          )}
        </FlexCol>
        {sortedGroups.map(([key, value]) => {
          const { label, locations, onlyShowIfPartExist } = value
          const slots = generateSlots(
            locations,
            onlyShowIfPartExist,
            components
          )
          const collapsibleTitle = generateTitles(slots, label)

          if (isEmpty(slots)) return null
          const groupKey = `${key}-${label}`
          return (
            <Group key={groupKey}>
              {collapsibleTitle && (
                <StyledCollapsibleWidget
                  collapsed={false}
                  title={collapsibleTitle}
                >
                  <StyledChecTable
                    columns={
                      defaultDrawerWidth
                        ? columns
                        : [
                            ...columns,
                            bomCodesColumn,
                            capPartNumberColumn,
                            capDescriptionColumn,
                          ]
                    }
                    dataSource={slots}
                    rowKey={(record) =>
                      `${record.panelNumber}${record.listLocation}-${groupKey}`
                    }
                    pagination={false}
                    className={`builder-swga-table-${key}-${label}`}
                    size="small"
                  />
                </StyledCollapsibleWidget>
              )}
            </Group>
          )
        })}
      </FlexCol>
    )
  })

  return (
    <>
      {unassignedComponentsTable}
      <FlexCol width="100%">{tables}</FlexCol>
    </>
  )
}

PartTableView.propTypes = {
  data: PropTypes.object,
  definitions: PropTypes.array,
  showPartDetails: PropTypes.func,
  onToggleListView: PropTypes.func,
}

export default PartTableView
